import { useEffect, useState } from 'react';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import { useCoordinatePatientsActions } from 'stores/patientFiltersStore';
import { LabelBold } from 'styles/typography';

import { CoordinateContentProps } from './contentProps';
import CoordinateEmpty from './CoordinateEmpty';
import PatientList from './CoordinatePatientList';
import CoordinateSearch from './CoordinateSearch';

/**
 * This component manages the animated rendering of the Coordinate desktop view.
 */
function CoordinateDesktopContent({
  patients,
  onAddToPortfolio,
  contentRequester,
  resultString = '',
  isLoading = false,
}: CoordinateContentProps) {
  const { setSearch } = useCoordinatePatientsActions();
  const isSearchCentered = !patients;

  const [shouldAnimate, setShouldAnimate] = useState(true);

  // If the user navigates back to this page by clicking 'Cancel'
  // on the intake form, do not play the animation again
  useEffect(() => {
    if (patients) setShouldAnimate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentContainer $hasResults={!!patients}>
      <SearchContainer $isCentered={isSearchCentered}>
        <CoordinateSearch onSearch={setSearch} isLoading={isLoading} />
      </SearchContainer>
      {patients && (
        <ResultsContainer $shouldAnimate={shouldAnimate}>
          {patients?.length ? (
            <>
              <ResultsLabel fontSize='14'>{resultString}</ResultsLabel>
              <PatientList {...{ patients, onAddToPortfolio }} />
              {contentRequester}
            </>
          ) : (
            //`flex` property added to make the page look a little nicer
            <CoordinateEmpty message={resultString} style={{ flex: 0.7 }} />
          )}
        </ResultsContainer>
      )}
    </ContentContainer>
  );
}

const ResultsLabel = styled(LabelBold)`
  margin-bottom: 24px;
`;

const ContentContainer = styled(Page.Content)<{ $hasResults: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SearchContainer = styled.div<{ $isCentered: boolean }>`
  display: flex;
  width: 100%;
  justify-content: center;
  transition: transform 0.75s cubic-bezier(0.4, 0, 0.2, 1);

  /* Center vertically using transform when no patients */
  transform: ${(props) => (props.$isCentered ? 'translateY(25vh)' : 'translateY(0)')};
`;

const ResultsContainer = styled.div<{ $shouldAnimate: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;
  gap: 8px;

  /* Fade and slide in animation - runs once on mount */
  animation: ${(props) => (props.$shouldAnimate ? 'fadeSlideIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) forwards' : 'none')};

  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
      flex: 0.9;
    }
    to {
      opacity: 1;
      transform: translateY(0);
      flex: 1;
    }
  }
`;

export default CoordinateDesktopContent;
